import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { merchantAuthAPI } from '../services/merchantAPI';

const TOKEN_KEY = 'merchant_access_token';

// The token is stored in localStorage AND mirrored to a cookie.
//
// localStorage is the real home: it has no expiry at all, and it is written to
// disk immediately - so the session survives Android killing the app, which is
// what used to log mobile users out after about a day. The cookie is only a
// mirror, kept because the backend reads it so plain <img src>/<video src> tags
// can authenticate without headers (see backend/app/__init__.py).
//
// Browsers cap how far out a cookie may be set, so it is given a long life AND
// re-stamped on every load - it can never be the thing that ends a session.
//
// Attributes are deliberately identical to the previous code (bare `expires`)
// so cookie behaviour is unchanged - only the number of days differs, 7 -> 365.
const COOKIE_DAYS = 365;

const readToken = () => {
  let stored = null;
  try {
    stored = localStorage.getItem(TOKEN_KEY);
  } catch (_) {
    // localStorage can throw in private mode / when storage is disabled
  }
  if (stored) return stored;

  // Fall back to the cookie and copy it into localStorage, so the next cold
  // start survives even if only the cookie was left.
  const fromCookie = Cookies.get(TOKEN_KEY);
  if (fromCookie) {
    try {
      localStorage.setItem(TOKEN_KEY, fromCookie);
    } catch (_) {}
    return fromCookie;
  }
  return null;
};

const writeToken = (token) => {
  if (!token) return;
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch (_) {}
  Cookies.set(TOKEN_KEY, token, { expires: COOKIE_DAYS });
};

const clearToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch (_) {}
  Cookies.remove(TOKEN_KEY);
};

// Runs once at import time, before React renders. Copies an existing
// cookie-only token into localStorage (so nobody already logged in gets kicked
// out by this change) and pushes the cookie expiry forward.
(() => {
  const existing = readToken();
  if (existing) writeToken(existing);
})();

const MerchantAuthContext = createContext();

export const useMerchantAuth = () => {
  const context = useContext(MerchantAuthContext);
  if (!context) {
    throw new Error('useMerchantAuth must be used within a MerchantAuthProvider');
  }
  return context;
};

export const MerchantAuthProvider = ({ children }) => {
  const [merchant, setMerchant] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token on app load. readToken() prefers localStorage and
    // falls back to the cookie, so the session survives the app being killed.
    const savedToken = readToken();
    const savedMerchantStr = localStorage.getItem('merchant') || Cookies.get('merchant');

    if (savedToken && savedMerchantStr) {
      try {
        setToken(savedToken);
        setMerchant(JSON.parse(savedMerchantStr));
      } catch (error) {
        // Corrupt cached profile: clear local state inline. Calling logout()
        // here would throw - it is declared as a const further down this file,
        // so it is not initialised yet when this effect runs.
        console.error('Error parsing saved merchant data:', error);
        setToken(null);
        setMerchant(null);
        clearToken();
        Cookies.remove('merchant');
        localStorage.removeItem('merchant');
      }
    }
    setLoading(false);
  }, []);

  // Periodic refresh of merchant data to keep credit balance updated
  // COMMENTED OUT: Automatic profile refresh disabled
  /*
  useEffect(() => {
    if (!token || !merchant) return;

    const refreshInterval = setInterval(async () => {
      try {
        const response = await merchantAuthAPI.getProfile();
        const updatedMerchant = response.data.merchant;
        
        // Only update if credit balance has changed
        if (updatedMerchant.credits_balance !== merchant.credits_balance) {
          setMerchant(updatedMerchant);
          Cookies.set('merchant', JSON.stringify(updatedMerchant), { expires: 7 });
        }
      } catch (error) {
        console.error('Periodic refresh error:', error);
      }
    }, 15000); // Refresh every 15 seconds

    return () => clearInterval(refreshInterval);
  }, [token, merchant]);
  */

  const register = async (registrationData) => {
    try {
      const response = await merchantAuthAPI.register(registrationData);
      return {
        success: true,
        data: response.data,
        message: response.data.message || 'Registration successful. Please check your email for OTP verification.'
      };
    } catch (error) {
      console.error('Registration error:', error);
      const data = error.response && error.response.data;
      return {
        success: false,
        error: (data && data.error) || 'Registration failed',
        field: data && data.field,
      };
    }
  };

  const setAuthSession = (merchantData, accessToken) => {
    setToken(accessToken);
    setMerchant(merchantData);

    // Token goes to localStorage AND a long-lived cookie mirror; merchant data
    // to localStorage only (avoids the 4KB cookie size limit).
    writeToken(accessToken);
    localStorage.setItem('merchant', JSON.stringify(merchantData));
  };

  const verifyOTP = async (email, otp) => {
    try {
      const response = await merchantAuthAPI.verifyOTP({ email, otp });

      // Check if the response contains the expected success data
      if (response.data && response.data.access_token && response.data.merchant) {
        const { access_token, merchant: merchantData } = response.data;

        setAuthSession(merchantData, access_token);

        return { success: true, message: 'Email verified successfully!' };
      } else {
        // If response doesn't contain expected data, treat as error
        return {
          success: false,
          error: response.data?.error || 'Invalid response from server'
        };
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      return {
        success: false,
        error: (error.response && error.response.data && error.response.data.error) || 'OTP verification failed'
      };
    }
  };

  const resendOTP = async (email) => {
    try {
      const response = await merchantAuthAPI.resendOTP(email);
      return {
        success: true,
        message: response.data.message || 'OTP sent successfully!'
      };
    } catch (error) {
      console.error('Resend OTP error:', error);
      return {
        success: false,
        error: (error.response && error.response.data && error.response.data.error) || 'Failed to send OTP'
      };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await merchantAuthAPI.login({ email, password });
      const { access_token, merchant: merchantData } = response.data;

      setAuthSession(merchantData, access_token);

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: (error.response && error.response.data && error.response.data.error) || 'Login failed'
      };
    }
  };

  const logout = async () => {
    try {
      await merchantAuthAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setMerchant(null);
      setToken(null);
      clearToken();
      Cookies.remove('merchant');
      localStorage.removeItem('merchant');
    }
  };

  const forgotPassword = async (email) => {
    try {
      const response = await merchantAuthAPI.forgotPassword(email);
      return {
        success: true,
        message: response.data.message || 'Password reset link sent to your email'
      };
    } catch (error) {
      console.error('Forgot password error:', error);
      return {
        success: false,
        error: (error.response && error.response.data && error.response.data.error) || 'Failed to send reset link'
      };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      const response = await merchantAuthAPI.resetPassword({ token, new_password: newPassword });
      return {
        success: true,
        message: response.data.message || 'Password reset successfully'
      };
    } catch (error) {
      console.error('Reset password error:', error);
      return {
        success: false,
        error: (error.response && error.response.data && error.response.data.error) || 'Password reset failed'
      };
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await merchantAuthAPI.updateProfile(profileData);
      const updatedMerchant = response.data.merchant;

      setMerchant(updatedMerchant);
      localStorage.setItem('merchant', JSON.stringify(updatedMerchant));

      return {
        success: true,
        merchant: updatedMerchant,
        message: 'Profile updated successfully'
      };
    } catch (error) {
      console.error('Update profile error:', error);
      return {
        success: false,
        error: (error.response && error.response.data && error.response.data.error) || 'Profile update failed'
      };
    }
  };

  const refreshProfile = async () => {
    try {
      const response = await merchantAuthAPI.getProfile();
      const updatedMerchant = response.data.merchant;

      setMerchant(updatedMerchant);
      localStorage.setItem('merchant', JSON.stringify(updatedMerchant));

      return { success: true, merchant: updatedMerchant };
    } catch (error) {
      console.error('Refresh profile error:', error);
      return { success: false };
    }
  };

  const isAuthenticated = () => {
    return !!(token && merchant && merchant.email_verified);
  };

  const isEmailVerified = () => {
    return merchant?.email_verified || false;
  };

  const value = {
    merchant,
    token,
    register,
    verifyOTP,
    resendOTP,
    login,
    logout,
    forgotPassword,
    resetPassword,
    updateProfile,
    refreshProfile,
    setAuthSession,
    isAuthenticated,
    isEmailVerified,
    loading
  };

  return (
    <MerchantAuthContext.Provider value={value}>
      {children}
    </MerchantAuthContext.Provider>
  );
}; 